/**
 * Page Panel
 *
 * Panel for static content and server-rendered HTML.
 *
 * @author Adam Tran <aviettran@gmail.com>
 * @license MIT
 */

import preact from "../js/lib/preact";
import { PS, PSRoom, type RoomOptions } from "./client-main";
import { PSPanelWrapper, PSRoomPanel } from "./panels";
import { BattleLog } from "./battle-log";
import { TL } from "./battle-dex";
import type { Args } from "./battle-text-parser";

export class SanitizedHTML extends preact.Component<{ children: string }> {
	override componentDidMount() {
		this.updateHTML(this.props.children);
	}
	override shouldComponentUpdate(nextProps: { children: string }) {
		if (nextProps.children !== this.props.children) this.updateHTML(nextProps.children);
		return false;
	}
	updateHTML(html: string) {
		this.base!.innerHTML = BattleLog.sanitizeHTML(html);
	}
	override render() {
		return <div></div>;
	}
}

class PageRoom extends PSRoom {
	override readonly classType: string = 'html';
	readonly page?: string = this.id.split("-")[1];
	override connectMode: PSRoom['connectMode'] = 'normal';

	loading = true;
	htmlData?: string;

	setHTMLData = (htmlData?: string) => {
		this.loading = false;
		this.htmlData = htmlData;
		this.update(null);
	};

	constructor(options: RoomOptions) {
		super(options);
		if (options.connectMode !== undefined) this.connectMode = options.connectMode;
		this.connect();
		this.title = this.id.split('-')[1];
	}
	override connect() {
		if (PagePanel.clientRooms.hasOwnProperty(this.id.split('-')[1])) {
			this.connectMode = null;
		} else if (!this.connected && (
			this.connectMode === 'normal' || this.connectMode === 'pending-reconnect' ||
			this.connectMode === 'pending-login'
		)) {
			PS.send(`/join ${this.id}`);
			this.connected = 'pending';
		}
	}
}

function PageLadderHelp() {
	const winLossNote = TL`Note that win/loss should not be used to estimate skill, since who you play against is much more important than how many times you win or lose. Our other stats like Elo and GXE are much better for estimating skill.`;
	return <div class="ladder pad">
		<p>
			<button class="button" data-href="/ladder" data-target="replace">
				<i class="fa fa-chevron-left" aria-hidden></i> {TL`[All formats]`}
			</button>
		</p>
		<h3>{TL`How the ladder works`}</h3>
		<p>
			{TL`Our ladder displays three ratings: Elo, GXE, and Glicko-1.`}
		</p>
		<p>
			{TL.parts(
				TL`{ELO} is the main ladder rating. It's a pretty normal ladder rating: goes up when you win and down when you lose.`,
				{ ELO: <strong>Elo</strong> }
			)}
		</p>
		<p>
			{TL.parts(
				TL`{GXE} (Glicko X-Act Estimate) is an estimate of your win chance against an average ladder player.`,
				{ GXE: <strong>GXE</strong> }
			)}
		</p>
		<p>
			{TL.parts(TL`{GLICKO} is {LINK}. It has rating and deviation values.`, {
				GLICKO: <strong>Glicko-1</strong>,
				LINK: <a href="https://en.wikipedia.org/wiki/Glicko_rating_system">{TL`another rating system`}</a>,
			})}
		</p>
		<p>
			{TL.parts(
				TL`{COIL} (Converging Order Invariant Ladder) is used for suspect tests. The more games you play, the closer it will get to your GXE × 4000. How fast it reaches GXE × 4000 depends on {LINK} which is different for each suspect test.`,
				{
					COIL: <strong>COIL</strong>,
					LINK: <a
						href="https://www.smogon.com/forums/threads/reintroducing-coil.3747719/" target="_blank"
					>{TL`a custom B value`}</a>,
				}
			)}
		</p>
		<p>
			{winLossNote}
		</p>
	</div>;
}

class PagePanel extends PSRoomPanel<PageRoom> {
	static readonly id = 'html';
	static readonly routes = ['view-*'];
	static readonly Model = PageRoom;
	static clientRooms: { [key: string]: JSX.Element } = { 'ladderhelp': <PageLadderHelp /> };

	/**
	 * @return true to prevent line from being sent to server
	 */
	override receiveLine(args: Args) {
		const { room } = this.props;
		switch (args[0]) {
		case 'title':
			room.title = args[1];
			PS.update();
			return true;
		case 'tempnotify': {
			const [, id, title, body] = args;
			room.notify({ title, body, id });
			return true;
		}
		case 'tempnotifyoff': {
			const [, id] = args;
			room.dismissNotification(id);
			return true;
		}
		case 'selectorhtml':
			const pageHTMLContainer = this.base!.querySelector('.page-html-container');
			const selectedElement = pageHTMLContainer?.querySelector(args[1]);
			if (!selectedElement) return;
			selectedElement.innerHTML = BattleLog.sanitizeHTML(args.slice(2).join('|'));
			room.subtleNotify();
			return true;
		case 'noinit':
			if (args[1] === 'namerequired') {
				room.setHTMLData(args[2]);
			}
			return true;
		case 'pagehtml':
			room.setHTMLData(args[1]);
			room.subtleNotify();
			return true;
		}
	}
	override render() {
		const { room } = this.props;
		let renderPage;
		if (room.page !== undefined && PagePanel.clientRooms[room.page]) {
			renderPage = PagePanel.clientRooms[room.page];
		} else {
			if (room.loading) {
				renderPage = <p>{TL`Loading...`}</p>;
			} else {
				renderPage = <div class="page-html-container">
					<SanitizedHTML>{room.htmlData || ''}</SanitizedHTML>
				</div>;
			}
		}
		return <PSPanelWrapper room={room}>
			{renderPage}
		</PSPanelWrapper>;
	}
}

PS.addRoomType(PagePanel);
